import { useEffect, useState } from "react";
import {
    Chart as ChartJS,
    ArcElement,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
} from "chart.js";
import BlogPosts from "../../components/Blogsposts";
import { API, Auth, graphqlOperation } from "aws-amplify";
import Updates from "./components/Updates";
import YoutubeEmbed from "../../components/YoutubeEmbed";
import DoughnutChart from "../../components/charts/DoughnutChart";
import BarChart from "../../components/charts/BarChart";
import useAnalyticsEventTracker from "../../tracker/EventTracker";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PiHandWaving } from "react-icons/pi";
import { TiChartPieOutline } from "react-icons/ti";
import axios from "axios";
import LoadingSpinner from "../../components/LoadingDocument";
import { RxUpdate } from "react-icons/rx";
import { GrBlog } from "react-icons/gr";
import { BiBook } from "react-icons/bi";
import { FaCrown } from "react-icons/fa6";
import { API_DEFAULT_HEADERS, API_DOMAIN } from "../../APIconfig";
import { plans } from "../../pages/profile/phone/data";
import CountdownClock from "../../components/CountDownClock";
import { Modal } from "../../components/Modal";
import Lottie from "lottie-react";
import crown from "../../lottie/crown.json";
import { Button } from "../../components/Button";
import { TrialExpirationChecker } from "../../components/TrialExpirationChecker";
import { Spinner } from "../../components/Spinner";
// Import skeleton components
import CardSkeleton from "../../components/Skeletons/CardSkeleton";
import ChartSkeleton from "../../components/Skeletons/ChartSkeleton";
import BlogSkeleton from "../../components/Skeletons/BlogSkeleton";
import UpdatesSkeleton from "../../components/Skeletons/UpdatesSkeleton";

interface Post {
    id: number;
    title: { rendered: string };
    excerpt: { rendered: string };
    content: { rendered: string };
    featured_media: number;
    guid: any;
    _embedded: { "wp:featuredmedia": FeaturedMedia[] };
    yoast_head_json: YoastHead;
}

interface FeaturedMedia {
    source_url: string;
}

interface YoastHead {
    keyword: string;
}

type MonthNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export const Dashboard = () => {
    const [fullName, setFullName] = useState<string>("");
    const [stripeID, setStripeID] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingStats, setLoadingStats] = useState<boolean>(true);
    const [loadingCharts, setLoadingCharts] = useState<boolean>(true);
    const [loadingBlogs, setLoadingBlogs] = useState<boolean>(true);
    const [loadingUpdates, setLoadingUpdates] = useState<boolean>(true);
    const [loadingTrial, setLoadingTrial] = useState<boolean>(false);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [leveringenData, setLeveringenData] = useState<any[]>([]);
    const [werkenData, setWerkenData] = useState<any[]>([]);
    const [dienstenData, setDienstenData] = useState<any[]>([]);
    const [posts, setPosts] = useState<Post[]>([]);
    const [updates, setUpdates] = useState<any[]>([]);
    const [tenderTypeData, setTenderTypeData] = useState<any[]>([]);
    const [totalTenders, setTotalTenders] = useState<number>(0);
    const [totalAwards, setTotalAwards] = useState<number>(0);
    const [isPremium, setIsPremium] = useState<boolean>(false);
    const [trialDateEnd, setTrialEndDate] = useState<any>("");
    const [subscription, setSubscription] = useState<boolean>(false);
    const [stripeStatus, setStripeStatus] = useState<string>("");

    const dutchMonths: Record<MonthNumber, string> = {
        1: "januari",
        2: "februari",
        3: "maart",
        4: "april",
        5: "mei",
        6: "juni",
        7: "juli",
        8: "augustus",
        9: "september",
        10: "oktober",
        11: "november",
        12: "december"
    };

    ChartJS.register(
        CategoryScale,
        LinearScale,
        BarElement,
        Title,
        Tooltip,
        ArcElement,
        Legend
    );

    useEffect(() => {
        document.title = "Dashboard | TenderTracer";
        checkPremium();
        fetchBlogPosts();
        EventTracker();
    }, []);

    function EventTracker() {
        useAnalyticsEventTracker("Dashboard", "Bekijkt Dashboard", "Dashboard");
    }

    function formatDate(dateString: string): string {
        const date = new Date(dateString);

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const seconds = String(date.getSeconds()).padStart(2, "0");

        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }

    const checkPremiumQuery = (userID: string) =>
        `query MyQuery { getUser(id: "${userID}") { firstname stripe_id lastname trial stripeStatus trialDateEnd companies { items { id company { active_subscription } } } } } `;

    const getCompanyIdQuery = (companyUserId: string) =>
        `query MyQuery { getCompanyUser(id: "${companyUserId}") { companyId } }`;

    const dashboardQuery = `
        query DashboardData($userId: ID!, $companyUserId: ID!, $country: String!, $year: Int!) {
        user: getUser(id: $userId) {
            firstname
            lastname
            stripe_id
            trial
            stripeStatus
            trialDateEnd
            companies {
            items {
                id
                company {
                id
                active_subscription
                }
            }
            }
        }
        companyUser: getCompanyUser(id: $companyUserId) {
            companyId
        }
        dashboardFeatures: searchDashboardFeatures(sort: {field: createdAt, direction: desc}, limit: 3) {
            items {
            title
            description
            coming_soon
            active
            createdAt
            }
        }
        generalStatistics: listGeneralStatistics(filter: { country: { eq: $country } }) {
            items {
                totalAwards
                totalTenders
                country
                year
            }
        }
        dashboardStatistics: listDashboardStatistics(filter: { 
            country: { eq: $country },
            active: { eq: true },
            year: { eq: $year }
        }
        limit: 1000
        ) {
            items {
                type
                name
                value
                country
                year
                month_number
            }
        }
        }
        `;
    
    const [hasUsedTrial, setHasUsedTrial] = useState<boolean>(false);
    
    async function checkPremium() {
        try {
            const { attributes } = await Auth.currentAuthenticatedUser();
            const currentYear = new Date().getFullYear();
            
            const response: any = await API.graphql(
                graphqlOperation(dashboardQuery, { 
                    userId: attributes.sub,
                    companyUserId: attributes.sub,
                    country: "NLD",
                    year: currentYear
                })
            );
    
            const data = response.data;
    
            // Process user data
            const userData = data.user;
            setFullName(userData.firstname);
            setStripeID(userData.stripe_id);
            setStripeStatus(userData.stripeStatus);
    
            const isUserActive = userData.companies.items[0]?.company.active_subscription || false;
            setIsPremium(isUserActive);
            setSubscription(isUserActive);
    
            // Process trial data
            const trialDateEnd = userData.trialDateEnd;
            if (trialDateEnd) {
                setHasUsedTrial(true);
                const trialEndDate = new Date(trialDateEnd);
                if (!isNaN(trialEndDate.getTime())) {
                    setTrialEndDate(trialEndDate);
                }
            } else {
                setHasUsedTrial(false);
            }
    
            // Process dashboard features
            setUpdates(data.dashboardFeatures?.items || []);
            setLoadingUpdates(false);
    
            // Process general statistics
            const generalStats = data.generalStatistics?.items?.reduce((latest: any, curr: any) => {
                if (!latest || parseInt(curr.year) > parseInt(latest.year)) {
                    return curr;
                }
                return latest;
            }, null) || {};
            
            setTotalAwards(generalStats.totalAwards || 0);
            setTotalTenders(generalStats.totalTenders || 0);
            setLoadingStats(false);
    
            // Process dashboard statistics
            const dashboardStats = data.dashboardStatistics?.items || [];
            const leveringen = dashboardStats.filter((item: any) => item.type.toLowerCase() === "leveringen");
            const werken = dashboardStats.filter((item: any) => item.type.toLowerCase() === "werken");
            const diensten = dashboardStats.filter((item: any) => item.type.toLowerCase() === "diensten");
            
            // Sort by month_number to ensure correct order
            const sortByMonth = (a: any, b: any) => a.month_number - b.month_number;
            
            const leveringenData = leveringen.sort(sortByMonth).map((item: any) => ({ 
                x: dutchMonths[item.month_number as MonthNumber] || "", 
                y: parseFloat(item.value) || 0 
            }));
            
            const werkenData = werken.sort(sortByMonth).map((item: any) => ({ 
                x: dutchMonths[item.month_number as MonthNumber] || "", 
                y: parseFloat(item.value) || 0 
            }));
            
            const dienstenData = diensten.sort(sortByMonth).map((item: any) => ({ 
                x: dutchMonths[item.month_number as MonthNumber] || "", 
                y: parseFloat(item.value) || 0 
            }));

            setLeveringenData(leveringenData);
            setWerkenData(werkenData);
            setDienstenData(dienstenData);

            // Calculate totals by type for doughnut chart
            const typeTotals = dashboardStats.reduce((acc: any, curr: any) => {
                const type = curr.type.toLowerCase();
                if (!acc[type]) {
                    acc[type] = {
                        name: curr.type,
                        value: 0
                    };
                }
                acc[type].value += parseFloat(curr.value) || 0;
                return acc;
            }, {});

            const tenderTypeData = Object.values(typeTotals);
            setTenderTypeData(tenderTypeData);
            setLoadingCharts(false);
            setLoading(false);
        } catch (error) {
            console.error("Error in checkPremium:", error);
            setLoadingStats(false);
            setLoadingCharts(false);
            setLoadingUpdates(false);
            setLoading(false);
        }
    }

    // Separate function to fetch blog posts
    async function fetchBlogPosts() {
        try {
            const blogResponse: any = await axios.get<Post[]>(
                "https://tendertracer.nl/index.php/wp-json/wp/v2/posts?_embed"
            );
            setPosts(blogResponse.data);
            setLoadingBlogs(false);
        } catch (error) {
            console.error("Error fetching blog posts:", error);
            setLoadingBlogs(false);
        }
    }

    async function initiateMonthlyPayment() {
        try {
            const session = await Auth.currentSession();
            const accessToken = session.getIdToken();
            const jwt = accessToken.getJwtToken();
            const monthlyPlan = plans.find(
                (plan) => plan.title === "Maandelijks"
            );
            if (!monthlyPlan) {
                console.error("Maandelijkse abonnement niet gevonden");
                return;
            }
            const raw = JSON.stringify({
                customerID: stripeID,
                priceID: monthlyPlan.id,
                locale: "nl",
                success_url: `${window.location.origin}/?payment_completed=true`,
                cancel_url: `${window.location.origin}/?payment_cancelled=true`,
            });
            const requestOptions: RequestInit = {
                method: "POST",
                headers: new Headers(API_DEFAULT_HEADERS(jwt)),
                body: raw,
            };
            const response = await fetch(
                `${API_DOMAIN}/stripe/pay`,
                requestOptions
            );
            const jsonResponse = await response.json();
            window.location.replace(jsonResponse.body);
        } catch (error) {
            console.log(error);
        }
    }
    async function checkSubscriptionStatus() {
        try {
            const { attributes } = await Auth.currentAuthenticatedUser();
            const userData: any = await API.graphql(
                graphqlOperation(checkPremiumQuery(attributes.sub))
            );
            const companyId =
                userData.data.getUser.companies.items[0].company.id;

            await API.graphql(
                graphqlOperation(
                    `mutation UpdateCompany{
                updateCompany(input: {id: "${companyId}", active_subscription: true}) {
                    id
                    active_subscription
                    }
          }`
                )
            );
            setSubscription(true);
            setIsPremium(true);
        } catch (error) {
            console.log("Error checking subscription status:", error);
        }
    }

    async function fetchTrial() {
        setLoadingTrial(true);
        try {
            const session = await Auth.currentSession();
            const accessToken = session.getIdToken();
            const jwt = accessToken.getJwtToken();

            // Haal de huidige user ID op

            var raw = JSON.stringify({
                customerID: stripeID,
                priceID: plans[0].id,
                locale: "nl",
                success_url: `${window.location.origin}/?trial_activated=true`,
                cancel_url: `${window.location.origin}/?trial_activated=false`,
            });

            var requestOptions: any = {
                method: "POST",
                headers: new Headers(API_DEFAULT_HEADERS(jwt)),
                body: raw,
            };

            const response = await fetch(
                `${API_DOMAIN}/stripe/trial`,
                requestOptions
            );
            const jsonResponse = await response.json();
            window.location.replace(jsonResponse.body);
        } catch (error) {
            setLoadingTrial(false);
        }
    }
    async function activateTrial() {
        try {
            const { attributes } = await Auth.currentAuthenticatedUser();
            const userData: any = await API.graphql(
                graphqlOperation(checkPremiumQuery(attributes.sub))
            );

            const companyUserId = userData.data.getUser.companies.items[0].id;
            const companyData: any = await API.graphql(
                graphqlOperation(getCompanyIdQuery(companyUserId))
            );

            const companyId = companyData.data.getCompanyUser.companyId;

            const userId = attributes.sub;

            const trialEndDate = new Date();
            trialEndDate.setDate(trialEndDate.getDate() + 45);
            const formattedDate = formatDate(trialEndDate.toISOString());

            await API.graphql(
                graphqlOperation(
                    `mutation UpdateUser($input: UpdateUserInput!) {
                        updateUser(input: $input) {
                            id
                            trialDateEnd
                        }
                    }`,
                    {
                        input: {
                            id: userId,
                            trialDateEnd: formattedDate,
                        },
                    }
                )
            );

            if (companyId) {
                await API.graphql(
                    graphqlOperation(
                        `mutation MyMutation { updateCompany(input: {id: "${companyId}", active_subscription: true}) { id active_subscription } }`
                    )
                );
            }
            setIsPremium(true);
            setTrialEndDate(trialEndDate);
            setHasUsedTrial(true);
            return Promise.resolve();
        } catch (error) {
            console.log("Error in activateTrial:", error);
            return Promise.reject();
        }
    }
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const trialActivated = urlParams.get("trial_activated");        
        const paymentCompleted = urlParams.get("payment_completed");

        if (trialActivated === "true") {
            activateTrial().then(() => {
                //window.location.replace("/");
                window.history.pushState(
                    {},
                    document.title,
                    window.location.pathname
                );
                window.location.reload();
            });
        } else if (paymentCompleted === "true") {
            checkSubscriptionStatus().then(() => {
                window.history.pushState(
                    {},
                    document.title,
                    window.location.pathname
                );
                window.location.reload();
            });
        }
    }, []);

    return (
        <>
            <TrialExpirationChecker />

            {loading ? (
                <div className="w-full h-full flex items-center justify-center">
                    {/* <LoadingDocument infiniteLoop={true} /> */}
                    <LoadingSpinner />
                </div>
            ) : (
                <section className="w-full h-full overflow-y-scroll">
                    <div className="flex justify-between p-2 items-center bg-white border shadow rounded-lg ml-5 mr-5 mt-2">
                        <div className="flex items-center">
                            <PiHandWaving className="text-4xl text-primary-pink" />
                            <h2 className="font-bold pl-2 text-xl">
                                Welkom terug, {fullName}!
                            </h2>
                        </div>
                        <div className="flex items-center">
                            <p className="text-xs opacity-30 pr-2">
                                Versie 4.0 |{" "}
                            </p>
                            <FontAwesomeIcon
                                className="text-[20px] text-primary-blue mr-2 hover:cursor-pointer hover:text-primary-pink"
                                icon={["fab", "youtube"]}
                                onClick={() =>
                                    window.open(
                                        "https://www.youtube.com/@TenderTracer"
                                    )
                                }
                            />
                            <FontAwesomeIcon
                                className="text-[20px] text-primary-blue mr-3 hover:cursor-pointer hover:text-primary-pink"
                                icon={["fab", "linkedin"]}
                                onClick={() =>
                                    window.open(
                                        "https://www.linkedin.com/company/tendertracer/"
                                    )
                                }
                            />
                        </div>
                    </div>
                    <div className="lg:grid grid-cols-6 mx-5">
                        <div className="col-span-4">
                            <div className="p-2 bg-white mt-2 w-full border shadow rounded-lg hidden items-center lg:flex">
                                <TiChartPieOutline className="text-2xl text-primary-blue" />
                                <h2 className="pl-2 font-bold text-lg">
                                    Laatste cijfers
                                </h2>
                            </div>
                            {loadingStats ? (
                                <div className="grid-cols-3 gap-4 hidden lg:grid">
                                    <CardSkeleton className="mt-2" />
                                    <CardSkeleton className="mt-2" />
                                    <ChartSkeleton className="mt-2" />
                                </div>
                            ) : (
                                <div className="grid-cols-3 gap-4 hidden lg:grid">
                                    <div className="mt-2 bg-white border shadow rounded-lg p-3">
                                        <p className="opacity-50 text-sm pb-3">
                                            Totaal aantal aanbestedingen
                                        </p>
                                        <div className="flex">
                                            <p className="text-5xl">
                                                {totalTenders?.toLocaleString(
                                                    undefined,
                                                    {
                                                        maximumFractionDigits: 2,
                                                    }
                                                )}
                                            </p>
                                            <FontAwesomeIcon
                                                className="text-[25px] text-primary-green"
                                                icon="arrow-trend-up"
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-2 bg-white border shadow rounded-lg p-3">
                                        <p className="opacity-50 text-sm pb-3">
                                            Totaal aantal gunningen
                                        </p>
                                        <div className="flex">
                                            <p className="text-5xl">
                                                {totalAwards?.toLocaleString(
                                                    undefined,
                                                    {
                                                        maximumFractionDigits: 2,
                                                    }
                                                )}
                                            </p>
                                            <FontAwesomeIcon
                                                className="text-[25px] text-primary-green"
                                                icon="arrow-trend-up"
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-2 bg-white border shadow rounded-lg p-3">
                                        <p className="opacity-50 text-sm pb-3">
                                            Type aanbestedingen
                                        </p>
                                        <DoughnutChart data={tenderTypeData} />
                                    </div>
                                </div>
                            )}
                            {loadingCharts ? (
                                <div className="grid-cols-3 gap-4 hidden lg:grid">
                                    <ChartSkeleton className="my-2" />
                                    <ChartSkeleton className="my-2" />
                                    <ChartSkeleton className="my-2" />
                                </div>
                            ) : (
                                <div className="grid-cols-3 gap-4 hidden lg:grid">
                                    <div className="my-2 bg-white border shadow rounded-lg p-2">
                                        <p className="opacity-50 text-sm pb-3">
                                            Aantal leveringen
                                        </p>
                                        <BarChart data={leveringenData} />
                                    </div>
                                    <div className="my-2 bg-white border shadow rounded-lg p-2">
                                        <p className="opacity-50 text-sm pb-3">
                                            Aantal diensten
                                        </p>
                                        <BarChart data={dienstenData} />
                                    </div>
                                    <div className="my-2 bg-white border shadow rounded-lg p-2">
                                        <p className="opacity-50 text-sm pb-3">
                                            Aantal werken
                                        </p>
                                        <BarChart data={werkenData} />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="lg:pl-3 w-full col-span-2">
                            <div>
                                <div className="p-2 mt-2 flex items-center bg-white border shadow rounded-lg">
                                    <RxUpdate className="text-2xl text-primary-blue" />
                                    <h2 className="pl-2 font-bold text-lg">
                                        Updates
                                    </h2>
                                </div>
                                {loadingUpdates ? (
                                    <UpdatesSkeleton />
                                ) : (
                                    <div className="mt-2">
                                        <Updates updates={updates} />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="col-span-4 mt-5">
                            <div className="flex items-center p-2 bg-white border shadow rounded-lg">
                                <GrBlog className="text-2xl text-primary-blue" />
                                <h2 className="pl-2 font-bold text-lg">
                                    Onze Blogs
                                </h2>
                            </div>
                            {loadingBlogs ? (
                                <BlogSkeleton />
                            ) : (
                                <div className="mt-1">
                                    <BlogPosts posts={posts} />
                                </div>
                            )}
                        </div>
                        <div className="col-span-2 mt-5 lg:pl-3">
                            <div className="flex items-center p-2 bg-white border shadow w-full rounded-lg h-auto">
                                <BiBook className="text-2xl text-primary-blue" />
                                <h2 className="pl-2 font-bold text-lg">
                                    Kennisbank
                                </h2>
                            </div>
                            <div
                                className="rounded-lg bg-white p-2 mt-2 hover:cursor-pointer hover:underline hover:shadow"
                                onClick={() => window.open("/help")}
                            >
                                <p className="text-sm">Hulp bij inschrijven</p>
                            </div>
                            <div
                                className="rounded-lg bg-white p-2 mt-1 hover:cursor-pointer hover:underline hover:shadow"
                                onClick={() =>
                                    window.open(
                                        "https://tendertracer.nl/aanbesteden"
                                    )
                                }
                            >
                                <p className="text-sm">Wat is aanbesteden</p>
                            </div>
                            <div
                                className="rounded-lg bg-white p-2 mt-1 hover:cursor-pointer hover:underline hover:shadow"
                                onClick={() =>
                                    window.open(
                                        "https://tendertracer.nl/mkbers-en-aanbestedingen-zo-maak-je-kans-op-succes/"
                                    )
                                }
                            >
                                <p className="text-sm">Hoe behaal ik succes</p>
                            </div>
                            <div className="bg-white mt-2 rounded-lg p-2">
                                <p>Uitleg video's</p>
                                <div className="grid grid-cols-2 gap-3 w-full pt-2">
                                    <div className="rounded-lg h-auto">
                                        <YoutubeEmbed embedId="paGUCNoeH-g" />
                                    </div>
                                    <div className="rounded-lg h-auto">
                                        <YoutubeEmbed embedId="uYfYt6L_4Bc" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {showModal && (
                        <Modal
                            title={
                                "Proefperiode 45 Dagen Gratis - TenderTracer Premium"
                            }
                            onClick={() => setShowModal(false)}
                        >
                            <div className="px-10 pt-10 relative">
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-25">
                                    <Lottie
                                        loop={false}
                                        animationData={crown}
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                        }}
                                    />
                                </div>
                                <div className="lg:grid grid-cols-4">
                                    <div className="relative z-10 col-span-3">
                                        <p className="font-bold">
                                            Bedankt voor je interesse in
                                            TenderTracer!
                                        </p>
                                        <p className="pt-3 font-bold font-sans">
                                            Instructies voor Activering van de
                                            45 Dagen Gratis Premiumperiode van
                                            TenderTracer
                                        </p>
                                        <ul className="list-disc pl-5 pt-2">
                                            <li>
                                                <span className="font-sans font-bold">
                                                    Klik op de knop voor de 45
                                                    dagen gratis Premium:
                                                </span>{" "}
                                                <span className="font-normal">
                                                    Hiermee krijg je 45 dagen
                                                    toegang tot TenderTracer
                                                    Premium, geheel kosteloos.
                                                </span>
                                            </li>
                                            <li>
                                                <span className="font-sans font-bold">
                                                    Registratie via Stripe:
                                                </span>{" "}
                                                <span className="font-normal">
                                                    Je wordt doorgestuurd naar
                                                    Stripe, om de volledig
                                                    gratis Premiumperiode
                                                    definitief te maken. Stopt
                                                    automatisch, geen verborgen
                                                    kosten!
                                                </span>
                                            </li>
                                            <li>
                                                <span className="font-sans font-bold">
                                                    Onboarding met AI:
                                                </span>{" "}
                                                <span className="font-normal">
                                                    Na het activeren van je
                                                    Premium proefperiode, helpt
                                                    onze AI-assistent je snel en
                                                    eenvoudig, binnen 5 minuten,
                                                    met het inregelen van
                                                    TenderTracer Premium.
                                                </span>
                                            </li>
                                        </ul>
                                        <p className="pt-3 font-bold font-sans">
                                            Na de 45 Dagen Proefperiode
                                        </p>
                                        <ul className="list-disc pl-5 pt-2">
                                            <li>
                                                <span className="font-sans font-bold">
                                                    Gratis Versie:
                                                </span>{" "}
                                                <span className="font-normal">
                                                    Kies je na 45 dagen niet
                                                    voor de premium versie, dan
                                                    stopt de proefperiode
                                                    automatisch en kun je
                                                    verder met de gratis lightversie
                                                    van TenderTracer.
                                                </span>
                                            </li>
                                            <li>
                                                <span className="font-sans font-bold">
                                                    Premium Versie:
                                                </span>{" "}
                                                <span className="font-normal">
                                                    Aan het einde van de
                                                    proefperiode kun je ook
                                                    kiezen om door te gaan met
                                                    onze premium versie, tegen
                                                    een betaling van € 35,- per
                                                    maand (exclusief btw)
                                                </span>
                                            </li>
                                        </ul>
                                        <p className="pt-3">
                                            Veel succes en plezier met het
                                            ontdekken van TenderTracer Premium!
                                        </p>
                                    </div>
                                    <div className="col-span-1 ml-5 pl-3 border-l border-gray-300">
                                        <p className="font-bold font-sans mb-2">
                                            Voordelen van TenderTracer Premium:
                                        </p>
                                        <ul className="space-y-2 text-sm">
                                            <li>
                                                <span className="font-semibold font-sans">
                                                    Voor u geselecteerde
                                                    aanbestedingen
                                                </span>
                                            </li>
                                            <li>
                                                <span className="font-semibold font-sans">
                                                    Internationale
                                                    aanbestedingen
                                                </span>
                                            </li>
                                            <li>
                                                <span className="font-semibold font-sans">
                                                    Uitgebreide concurrentie
                                                    analyse
                                                </span>
                                            </li>
                                            <li>
                                                <span className="font-semibold font-sans">
                                                    Hulp bij inschrijven
                                                </span>
                                            </li>
                                            <li>
                                                <span className="font-semibold font-sans">
                                                    Door AI gestuurde
                                                    CPV-codes*
                                                </span>
                                            </li>
                                        </ul>
                                        <br></br>
                                        <br></br>
                                        <p className="text-xs italic">
                                            *CPV-codes (Common Procurement
                                            Vocabulary) categoriseren
                                            onderwerpen van aanbestedingen
                                            binnen de EU.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            {loadingTrial ? (
                                <Button
                                    type="hidden"
                                    className="m-5 flex space-x-2 items-center justify-center"
                                >
                                    <Spinner />
                                    Laden...
                                </Button>
                            ) : (
                                <Button
                                    type="secondary"
                                    onClick={fetchTrial}
                                    className="animate-bounce m-5"
                                >
                                    Start gratis proefperiode
                                </Button>
                            )}
                        </Modal>
                    )}
                </section>
            )}
        </>
    );
};
